import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { DetailHeader } from '@/components/DetailHeader';
import { ActionGrid } from '@/components/ActionGrid';
import { ReminderControl } from '@/components/ReminderControl';
import { CallContextBar } from '@/components/CallContextBar';
import { SummaryBox } from '@/components/SummaryBox';
import { MessageBubble, type BubbleStatus } from '@/components/MessageBubble';
import { ReplyBar, type ReplyBarHandle } from '@/components/ReplyBar';
import { QuickReplySheet } from '@/components/QuickReplySheet';
import { ErrorState } from '@/components/ErrorState';
import { SkeletonDetail } from '@/components/Skeleton';
import { CelebrationBurst } from '@/components/CelebrationBurst';
import { Toast } from '@/components/Toast';
import { DEFAULT_SERVICE_PRESETS } from '@/lib/servicePresets';
import { useConversation } from '@/hooks/useConversation';
import { useReply } from '@/hooks/useReply';
import { useReviewRequest } from '@/hooks/useReviewRequest';
import { useMarkResolved } from '@/hooks/useMarkResolved';
import { useTakeover } from '@/hooks/useTakeover';
import { useStopDrip } from '@/hooks/useStopDrip';
import { useUnarchive } from '@/hooks/useUnarchive';
import { useMarkBooked, useUnmarkBooked } from '@/hooks/useMarkBooked';
import { useLogQuote } from '@/hooks/useLogQuote';
import { LogQuoteModal, type LogQuoteFormValues } from '@/components/LogQuoteModal';
import { markConversationRead } from '@/lib/readState';
import { markConversationReadRemote } from '@/lib/api';
import { resolveDisplayName } from '@/lib/nameFallback';
import { pillFor } from '@/lib/statusPills';
import type { ConversationStatus, ConversationSummary, Message } from '@/lib/api';

interface Toast {
  kind: 'success' | 'error';
  text: string;
  /** Optional undo affordance — shown on the right side of the toast.
   *  Used by the Archive action to let the user reverse the tag in GHL. */
  undo?: {
    label: string;
    onClick: () => void;
  };
}

function bubbleStatusFor(message: Message, lastFailedId: string | null): BubbleStatus {
  if (message.id.startsWith('optimistic_')) return 'pending';
  if (lastFailedId && message.id === lastFailedId) return 'failed';
  return 'sent';
}

/**
 * Returns 'Today', 'Yesterday', or a localized date label suitable for
 * use as a message-thread divider. Pure date comparison — ignores time.
 */
function dayLabelFor(iso: string): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  const d = new Date(ts);
  const dStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const oneDayMs = 86_400_000;
  const diffDays = Math.round((todayStart.getTime() - dStart.getTime()) / oneDayMs);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays > 0 && diffDays < 7) {
    return d.toLocaleDateString(undefined, { weekday: 'long' });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Same-day check that ignores time-of-day, used to gate divider rendering. */
function sameDay(a: string, b: string): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

// Item 5 (2026-06-05) — does the owner-facing summary contradict an
// actionable thread status? `summary_for_owner` is a single voice_ai_summary
// contact field that the MOST-RECENT call overwrites, so a thread that's
// "Needs you" (driven by an earlier needs_human / hot-lead call) can end up
// displaying the latest trivial hangup's "no action needed" summary —
// "Needs you" + "no action needed" at once (Ernie 2026-06-05, Item 5).
// We treat it as a contradiction ONLY when the status is actionable
// (attention) AND the summary explicitly says no action / follow-up is
// needed; in that case the summary is from the wrong call and gets
// suppressed, leaving the "Needs you" pill + the transcript to carry the
// state. Patterns are deliberately narrow so a genuinely useful summary is
// never hidden.
const NO_ACTION_SUMMARY =
  /\bno\s+(?:further\s+)?action\b|\bnothing\s+(?:further\s+)?(?:needed|required|to\s+do)\b|\bno\s+follow[\s-]?up\b/i;

function summaryContradictsActionableStatus(
  status: ConversationStatus,
  summary: string | undefined | null,
): boolean {
  if (status !== 'attention') return false;
  const s = (summary ?? '').trim();
  if (!s) return false;
  return NO_ACTION_SUMMARY.test(s);
}

// Part B (2026-06-05) — friendly "sends ~Xam" label for a queued recap. The
// recap landed off the send window and is held in the Worker's deferred-tag
// queue; this distinguishes "queued, on its way" from "failed/ghosted".
function formatRecapSendsAt(iso: string | null | undefined): string {
  if (!iso) return 'sends shortly';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'sends shortly';
  if (t - Date.now() <= 2 * 60_000) return 'sends shortly';
  return `sends ~${new Date(t).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

// Render E.164 phones as "(XXX) XXX-XXXX" for US numbers; anything else
// passes through unchanged.
function formatPhone(raw: string): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

/**
 * ConversationDetail — the heart of the PWA per the locked comms-pivot spec.
 *
 * Layout (top → bottom, mock parity):
 *   1. DetailHeader  — sticky white bar, back chevron + name + meta
 *   2. ActionGrid    — Call back / Take over (primary) / Review req
 *   3. SummaryBox    — Shelby's owner-facing summary
 *   4. TranscriptCard — collapsible call transcript (closed by default)
 *   5. Thread        — bubbles (Shelby vs SMS in vs SMS out)
 *   6. ReplyBar      — sticky bottom input (positioned above tab bar)
 *
 * The body scroll container reserves space for the reply bar AND the bottom
 * tab bar so the last bubble is never hidden underneath either.
 */
export function ConversationDetail() {
  const { slug = '', id = '' } = useParams<{ slug: string; id: string }>();
  const navigate = useNavigate();
  // useTenant() previously supplied tenant.location_id for the GHL
  // deep-link in ActionGrid — that path was removed when Take Over became
  // an in-app Shelby pause (2026-05-12), so no tenant read is needed.
  const qc = useQueryClient();

  const conversationQuery = useConversation(slug, id);
  const reply = useReply(slug, id);
  const reviewRequest = useReviewRequest(slug);
  const markResolved = useMarkResolved(slug, id);
  const unarchive = useUnarchive(slug, id);
  const markBooked = useMarkBooked(slug, id);
  const unmarkBooked = useUnmarkBooked(slug, id);
  const takeover = useTakeover(slug, id);
  const stopDrip = useStopDrip(slug, id);
  const logQuote = useLogQuote(slug, id);

  // 2026-06-30 (NotificationDeepLink) — a push notification deep-link carries the
  // CONTACT id in the URL (/conversations/:contactId). The Worker resolves it to
  // the canonical conversation and returns its conversation_id as data.id. Once
  // loaded, replace the URL with the canonical conversationId so reply / mark-read
  // / takeover / etc. (all keyed on the URL :id) target the right conversation.
  const canonicalConversationId = conversationQuery.data?.id;
  useEffect(() => {
    if (canonicalConversationId && id && canonicalConversationId !== id) {
      navigate(`/${slug}/conversations/${canonicalConversationId}`, { replace: true });
    }
  }, [canonicalConversationId, id, slug, navigate]);

  // Mark this conversation as locally read whenever the detail loads or a
  // new message arrives in-thread. Two effects:
  //   1. Persist to localStorage so the Conversations list can suppress the
  //      blue unread dot on subsequent renders / refetches.
  //   2. Patch any cached ConversationSummary entries in React Query so the
  //      list reflects "read" the moment the user navigates back, without
  //      waiting for a refetch.
  //
  // Ernie 2026-05-03 round 2: the v1 used the detail's lastMessage timestamp
  // as the read marker. That broke for rows where the conversation list's
  // last_message timestamp was NEWER than the detail's last visible message
  // (e.g. detail filters out system rows that the list still surfaces). Now
  // we mark with "now" — anything older than this open is considered read,
  // and any genuinely new message arriving later will have timestamp > now,
  // correctly re-firing the unread dot.
  const messages = conversationQuery.data?.messages ?? [];
  const messageCount = messages.length;
  // 2026-06-10 EVE (parked batch, Item D2) — fire the REMOTE mark-read once
  // per (conversation open, newest message), not on every loading-state flip.
  // Live tail showed GET + mark-read POST alternating ~2s on an open thread —
  // each POST is a D1 write. The local mark stays instant; the remote mark is
  // guarded by a last-marked ref keyed on id + newest message identity.
  const lastRemoteMarkRef = useRef<string>('');
  useEffect(() => {
    if (!id) return;
    if (conversationQuery.isLoading) return;
    const now = Date.now();
    const nowIso = new Date(now).toISOString();
    markConversationRead(id, nowIso);
    const newest = messages[messages.length - 1] as { timestamp_iso?: string } | undefined;
    const remoteKey = `${id}:${newest?.timestamp_iso ?? ''}:${messageCount}`;
    if (lastRemoteMarkRef.current !== remoteKey) {
      lastRemoteMarkRef.current = remoteKey;
      // Persist the read mark server-side too (durable + cross-device). The
      // local mark above gives instant UI; this is best-effort and silent on
      // failure — the dot just lingers until the next open.
      void markConversationReadRemote(slug, id, now).catch(() => {});
    }
    // Patch any cached conversations list (any filter / search variant) to
    // flip this convo's `unread` to false so the back-nav UI is instant.
    //
    // 2026-06-01 — previous version walked `page.items` but the
    // ConversationsListPage type stores rows under `page.conversations`, so
    // the patch silently no-op'd and the blue dot stayed on the row after
    // back-nav. Fixed to match the working pattern in Conversations.tsx +
    // the actual API contract (api.ts returns `{ conversations, next_offset }`
    // pages). Audit caught this on 2026-06-01.
    qc.setQueriesData<{ pages?: Array<{ conversations?: ConversationSummary[] }> }>(
      { queryKey: ['conversations', slug] },
      (old) => {
        if (!old?.pages) return old;
        let mutated = false;
        const pages = old.pages.map((page) => {
          if (!page?.conversations) return page;
          const conversations = page.conversations.map((c) => {
            if (c.id === id && c.unread) {
              mutated = true;
              return { ...c, unread: false };
            }
            return c;
          });
          return mutated ? { ...page, conversations } : page;
        });
        return mutated ? { ...old, pages } : old;
      },
    );
  }, [id, slug, messageCount, conversationQuery.isLoading, qc]);

  const [toast, setToast] = useState<Toast | null>(null);
  const [lastFailed, setLastFailed] = useState<{ id: string; body: string } | null>(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [logQuoteOpen, setLogQuoteOpen] = useState(false);
  const replyBarRef = useRef<ReplyBarHandle | null>(null);
  // Triggered when a meaningful win happens — Resolved / Booked / Review-sent.
  // Plays the radial-burst celebration component once, then resets.
  const [celebration, setCelebration] = useState<{
    centerLabel: string;
    subLabel: string;
  } | null>(null);

  // Auto-clear toasts after a few seconds.
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(t);
  }, [toast]);

  // Keep the thread scrolled to the latest message as it grows.
  // Scrolls the messages region (not the page). On initial mount we use
  // 'auto' + 2x rAF so async layout settles before measuring; subsequent
  // new-message scrolls use 'smooth' for affordance.
  const messagesScrollRef = useRef<HTMLDivElement | null>(null);
  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const initialScrollDoneRef = useRef(false);
  useEffect(() => {
    if (messageCount === 0) return;
    const isInitial = !initialScrollDoneRef.current;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const container = messagesScrollRef.current;
        if (!container) return;
        if (isInitial) {
          container.scrollTop = container.scrollHeight;
        } else {
          container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        }
        initialScrollDoneRef.current = true;
      });
    });
  }, [messageCount]);

  const handleSendReply = (body: string) => {
    setLastFailed(null);
    reply.mutate(
      { body },
      {
        onError: (err) => {
          setLastFailed({ id: `failed_${Date.now()}`, body });
          setToast({
            kind: 'error',
            text: err instanceof Error ? `Send failed: ${err.message}` : 'Send failed',
          });
        },
      },
    );
  };

  const handleRetryFailed = () => {
    if (!lastFailed) return;
    handleSendReply(lastFailed.body);
  };

  const handleReviewRequest = () => {
    const contactId = conversationQuery.data?.contact.id;
    if (!contactId) return;
    reviewRequest.mutate(
      { contactId },
      {
        onSuccess: () => {
          setToast({ kind: 'success', text: 'Review request sent' });
          setCelebration({ centerLabel: '⭐ Sent', subLabel: 'Watch for the review' });
        },
        onError: (err) =>
          setToast({
            kind: 'error',
            text: err instanceof Error ? `Failed: ${err.message}` : 'Failed to send review request',
          }),
      },
    );
  };

  const handleTakeOver = () => {
    takeover.mutate(undefined, {
      onSuccess: () => {
        setToast({ kind: 'success', text: 'Shelby paused. You can text from here.' });
        // Drop focus into the reply bar so the owner can type immediately.
        replyBarRef.current?.focus?.();
      },
      onError: (err) =>
        setToast({
          kind: 'error',
          text: err instanceof Error ? `Take over failed: ${err.message}` : 'Take over failed',
        }),
    });
  };

  const handleStopDrip = () => {
    stopDrip.mutate(undefined, {
      onSuccess: () => {
        setToast({ kind: 'success', text: 'Drip stopped. Shelby still replies to inbound texts.' });
      },
      onError: (err) =>
        setToast({
          kind: 'error',
          text: err instanceof Error ? `Stop drip failed: ${err.message}` : 'Stop drip failed',
        }),
    });
  };

  const handleUnarchive = () => {
    unarchive.mutate(undefined, {
      onSuccess: () =>
        setToast({ kind: 'success', text: 'Restored' }),
      onError: (err) =>
        setToast({
          kind: 'error',
          text: err instanceof Error ? `Unarchive failed: ${err.message}` : 'Unarchive failed',
        }),
    });
  };

  const handleUnmarkBooked = () => {
    unmarkBooked.mutate(undefined, {
      onSuccess: () =>
        setToast({ kind: 'success', text: 'Booking removed' }),
      onError: (err) =>
        setToast({
          kind: 'error',
          text: err instanceof Error ? `Undo failed: ${err.message}` : 'Undo failed',
        }),
    });
  };

  const handleMarkBooked = () => {
    markBooked.mutate(undefined, {
      onSuccess: () => {
        setToast({
          kind: 'success',
          text: 'Marked booked',
          undo: { label: 'Undo', onClick: handleUnmarkBooked },
        });
        setCelebration({ centerLabel: '✓ Booked', subLabel: 'Nicely done' });
      },
      onError: (err) =>
        setToast({
          kind: 'error',
          text: err instanceof Error ? `Mark booked failed: ${err.message}` : 'Mark booked failed',
        }),
    });
  };

  const handleLogQuoteSubmit = (values: LogQuoteFormValues) => {
    logQuote.mutate(
      {
        amount: values.amount,
        service_label: values.service_label,
        vehicle: values.vehicle,
        send_recap: values.send_recap,
      },
      {
        onSuccess: (result) => {
          setLogQuoteOpen(false);
          setToast({
            kind: 'success',
            text: result.recap_triggered
              ? 'Quote logged + recap on the way'
              : 'Quote logged',
          });
          setCelebration({
            centerLabel: '✓ Quoted',
            subLabel: result.recap_triggered ? 'Recap sent' : 'Saved to contact',
          });
        },
        onError: (err) =>
          setToast({
            kind: 'error',
            text: err instanceof Error ? `Log quote failed: ${err.message}` : 'Log quote failed',
          }),
      },
    );
  };

  const handleMarkResolved = () => {
    markResolved.mutate(
      {},
      {
        onSuccess: () => {
          setToast({
            kind: 'success',
            text: 'Archived',
            // Undo affordance — Stripe pattern. Toast auto-clears after
            // ~3.5s; user can tap Undo to call the unarchive endpoint
            // (removes pwa_resolved tag from the contact in GHL).
            undo: { label: 'Undo', onClick: handleUnarchive },
          });
          // No CelebrationBurst here — archive isn't a "win", it's just
          // clearing a thread off the plate. The big radial burst + center
          // card overlapped the conversation content and felt redundant
          // with the bottom toast (Ernie 2026-05-24, on-device). Burst stays
          // on the actual wins: Mark Booked + Review Request sent.
        },
        onError: (err) =>
          setToast({
            kind: 'error',
            text: err instanceof Error ? `Failed: ${err.message}` : 'Failed to archive',
          }),
      },
    );
  };

  // --- Render branches ---------------------------------------------------
  if (conversationQuery.isLoading) {
    return <SkeletonDetail />;
  }

  if (conversationQuery.isError || !conversationQuery.data) {
    return (
      <div className="bg-white dark:bg-[#0B1220]">
        <DetailHeader name="Conversation" meta="" fallbackSlug={slug} />
        <ErrorState
          title="Couldn’t load conversation"
          message={
            conversationQuery.error instanceof Error
              ? conversationQuery.error.message
              : 'Please try again.'
          }
          onRetry={() => void conversationQuery.refetch()}
        />
      </div>
    );
  }

  const conv = conversationQuery.data;

  // Item 5 — suppress a stale latest-call summary that contradicts an
  // actionable status (see summaryContradictsActionableStatus). Both the
  // pinned Summary chip and the in-thread SummaryBox read this so they never
  // pair "Needs you" with a "no action needed" line.
  const displaySummary = summaryContradictsActionableStatus(
    conv.status,
    conv.summary_for_owner,
  )
    ? ''
    : conv.summary_for_owner;

  return (
    <div className="flex h-full min-h-0 flex-col bg-ink-50 dark:bg-[#0B1220] animate-ss-slide-up">
      {/* PINNED top — header + action grid + (when present) Shelby's call
          context (summary + collapsed transcript chip). Lives as a single
          shrink-0 flex sibling so context never scrolls away with the SMS
          thread — Ernie 2026-05-12: "we want it to stand out and not
          disappear." Per ChatGPT R1 consult 2026-05-03: in a fixed-viewport
          app shell where each screen owns its own scroll, "pinned" means
          flex-none siblings around a flex-1 scroll body — NOT
          position:sticky inside a nested overflow container (which iOS
          standalone PWA breaks). The soft shadow lifts the pinned layer
          visually above the SMS thread underneath. */}
      <div
        className="shrink-0 border-b border-ink-100 dark:border-[#334155] bg-white dark:bg-[#0F172A] shadow-[0_4px_12px_-6px_rgba(15,23,42,0.18)]"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        {(() => {
          // Resolve smart display name + status pill so the header reads
          // premium even when GHL gave us "Unknown" / "" for the contact
          // (R11 consult 2026-05-06).
          const { displayName, isFallback } = resolveDisplayName({
            contactName: conv.contact.name,
            contactPhone: conv.contact.phone,
          });
          const lastMsgSource = messages.length
            ? messages[messages.length - 1]?.source
            : undefined;
          const pill = pillFor(conv.status, lastMsgSource);
          const meta = [
            formatPhone(conv.contact.phone),
            conv.contact.vehicle_or_meta,
          ]
            .filter(Boolean)
            .join(' · ');
          const transcriptCount =
            (conv.transcripts && conv.transcripts.length) ||
            (conv.transcript ? 1 : 0);
          const hasCallContext =
            !!(displaySummary ?? '').trim() || transcriptCount > 0;
          return (
            <>
              <DetailHeader
                name={displayName}
                nameItalic={isFallback}
                meta={meta}
                pillLabel={pill.label}
                pillClass={pill.className}
                fallbackSlug={slug}
                dripActive={conv.is_workflow_active === true}
                dripQueuedLabel={
                  conv.drip_pending?.queued
                    ? formatRecapSendsAt(conv.drip_pending.sends_at_iso)
                    : null
                }
                onStopDrip={handleStopDrip}
                dripBusy={stopDrip.isPending}
              />

              <ActionGrid
                contactPhone={conv.contact.phone}
                contactName={displayName}
                onTakeOver={handleTakeOver}
                takeOverBusy={takeover.isPending}
                onReviewRequest={handleReviewRequest}
                reviewBusy={reviewRequest.isPending}
                onMarkBooked={handleMarkBooked}
                markBookedBusy={markBooked.isPending}
                alreadyBooked={conv.status === 'booked'}
                onUnmarkBooked={handleUnmarkBooked}
                unmarkBookedBusy={unmarkBooked.isPending}
                onMarkResolved={handleMarkResolved}
                onUnarchive={handleUnarchive}
                resolvedBusy={markResolved.isPending}
                unarchiveBusy={unarchive.isPending}
                alreadyResolved={conv.status === 'resolved'}
                onLogQuote={() => setLogQuoteOpen(true)}
                logQuoteBusy={logQuote.isPending}
              />

              {/* Call context — compact chip row (Summary + Transcript
                  pills). Tap a chip to open the full content as a
                  bottom-sheet over the SMS thread. Keeps the pinned
                  region tiny (~48px) so the thread isn't crowded, while
                  still surfacing call context at a glance. */}
              {/* 2026-07-02 (ReminderPillInline) — the reminder pill lives
                  INSIDE the chip row, after Summary/Transcript. The bar now
                  renders even without call context so the "Remind me" add
                  affordance always has a home in that row. */}
              <CallContextBar
                summary={hasCallContext ? displaySummary : ''}
                transcripts={hasCallContext ? conv.transcripts : undefined}
                transcript={hasCallContext ? conv.transcript : null}
                trailing={
                  <ReminderControl
                    slug={slug}
                    contactId={conv.contact.id}
                    conversationId={conv.id}
                    contactLabel={displayName}
                    messages={conv.messages.map((m) => ({ body: m.body, direction: m.direction }))}
                  />
                }
              />

              {/* Part B — queued recap indicator. A customer-requested booking
                  link / recap that landed off the send window is held in the
                  Worker's deferred-tag queue; surface it so the owner knows
                  it's on its way, not failed. Only shows when the Worker
                  reports recap_pending.queued (post-consult Worker deploy). */}
              {conv.recap_pending?.queued && (
                <div className="mx-4 mb-3 flex items-center gap-2 rounded-ss-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] font-medium text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-3.5 w-3.5 flex-shrink-0"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <polyline points="12 7 12 12 15 14" />
                  </svg>
                  <span>
                    Booking link queued — {formatRecapSendsAt(conv.recap_pending.sends_at_iso)}
                  </span>
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* SCROLLABLE middle — SMS thread only. flex-1 + min-h-0 lets it
          own the only scroll context inside this screen. */}
      <div
        ref={messagesScrollRef}
        className="min-h-0 flex-1 overflow-y-auto pt-3"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <div className="px-4 pb-4 pt-2">
          {/* Shelby's summary as the first item in the thread — scrolls up
              out of view as the conversation scrolls (iMessage-style). The
              Summary chip in the pinned header above is the quick re-access
              after scrolling. Self-hides when there's no summary (text-only
              leads). Ernie 2026-05-22. */}
          <SummaryBox text={displaySummary} />
          {conv.messages.map((m, i) => {
            const prev = i > 0 ? conv.messages[i - 1] : null;
            const showDivider = !prev || !sameDay(prev.timestamp_iso, m.timestamp_iso);
            return (
              <div key={m.id}>
                {showDivider ? (
                  <div className="flex items-center justify-center py-3">
                    <span className="rounded-full bg-ink-100 dark:bg-[#1E293B] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-ink-500 dark:text-[#94A3B8]">
                      {dayLabelFor(m.timestamp_iso)}
                    </span>
                  </div>
                ) : null}
                <MessageBubble
                  message={m}
                  status={bubbleStatusFor(m, lastFailed?.id ?? null)}
                />
              </div>
            );
          })}
          <div ref={threadEndRef} />
        </div>
      </div>

      {/* PINNED bottom — reply bar as another shrink-0 flex sibling
          (NOT position:fixed). Sits naturally above the AppShell's tab
          bar via the AppShell flex column hierarchy. */}
      <ReplyBar
        ref={replyBarRef}
        onSend={handleSendReply}
        busy={reply.isPending}
        leftSlot={
          <button
            type="button"
            onClick={() => setTemplatesOpen(true)}
            aria-label="Insert quick reply template"
            className="ss-press flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-ink-100 dark:bg-[#1E293B] text-ink-700 dark:text-[#94A3B8]"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
              <path d="M3 6h18M3 12h12M3 18h18" />
            </svg>
          </button>
        }
      />
      <QuickReplySheet
        open={templatesOpen}
        slug={slug}
        onClose={() => setTemplatesOpen(false)}
        onPick={(body) => replyBarRef.current?.setText(body)}
      />

      {/* Manual quote entry — used when the owner picked up the call
          themselves (off-Shelby) and wants to record the quote so the
          contact card + reports reflect it, optionally firing the
          recap text + drip. Presets are DP-specific for now; once SS
          tenant config exposes services we can read them from there. */}
      <LogQuoteModal
        open={logQuoteOpen}
        contactName={conv.contact.name || 'this contact'}
        busy={logQuote.isPending}
        servicePresets={[...DEFAULT_SERVICE_PRESETS]}
        onCancel={() => {
          if (logQuote.isPending) return;
          setLogQuoteOpen(false);
        }}
        onConfirm={handleLogQuoteSubmit}
      />

      {/* Toast — sliding bottom-sheet pill (Stripe / Linear pattern).
          Positioned above the reply bar (~52px) and the tab bar. */}
      {toast && (
        <Toast
          kind={toast.kind}
          text={toast.text}
          bottomOffset="calc(var(--tab-h, 64px) + 64px)"
          action={
            toast.kind === 'error' && lastFailed
              ? { label: 'Retry', onClick: handleRetryFailed }
              : toast.undo
                ? {
                    label: toast.undo.label,
                    onClick: () => {
                      toast.undo?.onClick();
                      setToast(null);
                    },
                  }
                : undefined
          }
        />
      )}

      {/* Conversion celebration — radial burst + center label, plays once */}
      <CelebrationBurst
        trigger={celebration !== null}
        centerLabel={celebration?.centerLabel}
        subLabel={celebration?.subLabel}
        onDone={() => setCelebration(null)}
      />
    </div>
  );
}
